import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { format } from "date-fns";
import { BarChartProps } from "@repo/core/components/chart/ReportCharts/BarChart/types";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { useQuery } from "@tanstack/react-query";

const reportsResource = new ReportsResource(newApInstance);

export const OverdueWalletReportController = () => {
  const { isMobile } = useDimensions();
  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const filterModalRef = useRef<IModalRefProps>(null);

  const columnHelper = createColumnHelper();

  const {
    data: overdueWalletReportResponse,
    isFetching: isFetchingOverdueWalletReport
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "attendants",
      filterStartDate,
      filterEndDate,
      selectedStore,
      selectedProduct,
      selectedAttendant
    ],
    queryFn: () =>
      reportsResource.getAttendantsReports({
        startDate: format(filterStartDate, "yyyy-MM-dd"),
        endDate: format(filterEndDate, "yyyy-MM-dd"),
        store: selectedStore,
        product: selectedProduct,
        attendant: selectedAttendant
      })
  });
  const headerColumns = overdueWalletReportResponse?.data.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const { tableData, hasReportData } = useMemo(() => {
    const tableData = convertDataRows(
      overdueWalletReportResponse?.data?.table.headers,
      overdueWalletReportResponse?.data?.table.rows
    );

    const hasReportData =
      Array.isArray(overdueWalletReportResponse?.data?.table.rows) &&
      overdueWalletReportResponse.data.table.rows.length > 0;

    return {
      tableData,
      hasReportData
    };
  }, [overdueWalletReportResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      "Relatório_Carteira_a_Receber_X_Em_Atraso",
      overdueWalletReportResponse?.data?.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  const customStyles: BarChartProps["customStyles"] = {
    barStyle: { width: 42 },
    groupOffset: 48,
    domainPadding: 60
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    filterModalRef,
    topRightText,
    customStyles,
    filterStartDate,
    filterEndDate,
    hasReportData,
    handleSelectFilters,
    isFetchingOverdueWalletReport
  };
};
